// Package imports:
import React, { useMemo, useState } from "react";
import { Form, Formik, useFormikContext } from "formik";
import { faTrashCan } from "@fortawesome/pro-solid-svg-icons";
import Bugsnag from "@bugsnag/js";
// Component imports:
import Modal from "../../ui-elements/Modal/Modal";
import Button from "../../ui-elements/Button/Button";
import Alert, { AlertType } from "../../ui-elements/Alert/Alert";
import Input from "../../ui-elements/Input/Input";
import FormikInput from "../../ui-elements/Input/Formik/Input";
import { DatePickerField } from "../../ui-elements/DatePicker/Formik/DatePicker";
// Service imports:
import { GET_KELDAN_API_URL } from "../../services/config";
// Type imports:
import { IDueDiligenceModalProps, KnowYourIndividual, RiskScoreDto, ServiceCompanyEmployee } from "../../types/DueDiligenceTypes";
// Static data imports:
import { CountryList } from "../../assets/staticData/CountryList";
import { Fetched } from "../../types/Types";
import DropSelectSearch from "../../ui-elements/DropSelect/Formik/DropSelectSearch";
import DropSelect from "../../ui-elements/DropSelect/DropSelect";

interface IProps extends IDueDiligenceModalProps {
    data: KnowYourIndividual | null;
    individuals?: Fetched<KnowYourIndividual[]>;
    setIndividuals?: React.Dispatch<React.SetStateAction<Fetched<KnowYourIndividual[]>>>;
    sendNotification?: boolean;
    riskScore?: RiskScoreDto;
    setRiskScore?(riskScore: RiskScoreDto): void;
    employees: ServiceCompanyEmployee[];
}

interface IFormValues {
    id: number;
    name: string;
    address: string;
    areaCode: string;
    region: string;
    country: string;
    nationality: string;
    email: string;
    foreign: boolean;
    ssn: string;
    dateOfBirth?: string | null;
    supervisorId?: number | null;
}

const SubFormComponent: React.FC<{
    values: IFormValues;
    data: KnowYourIndividual;
    employees: ServiceCompanyEmployee[];
    close(): void;
    onDelete(): void;
    setToastShow(show: boolean): void;
    setToastType(type: AlertType): void;
    setToastMessage(message: string): void;
    updateIndividuals(values: IFormValues, deleted?: boolean): void;
    disableSubmit: boolean;
    setDisableSubmit(disable: boolean): void;
    sendNotification?: boolean;
}> = ({
    values,
    data,
    employees,
    close,
    onDelete,
    setToastShow,
    setToastType,
    setToastMessage,
    updateIndividuals,
    disableSubmit,
    setDisableSubmit,
    sendNotification
}) => {
        const { setFieldValue, getFieldMeta, } = useFormikContext<IFormValues>();
        const countryCodeFieldMeta = getFieldMeta("country");

        const [confirmDeleteModalOpen, setConfirmDeleteModalOpen] = useState(false);

        const handleDelete = async () => {
            try {
                setDisableSubmit(true);
                const url = `${GET_KELDAN_API_URL()}/DueDiligence/RemoveIndividual/${values.id}`;
                const response = await fetch(url, {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                    }
                });
                setDisableSubmit(false);

                if (!response.ok) {
                    const errorData = await response.text();
                    setToastMessage(errorData || "Ekki tókst að eyða einstaklingi");
                    setToastType("error");
                    setToastShow(true);
                    return;
                }
                updateIndividuals(values, true);
                setToastMessage("Einstaklingi eytt");
                setToastType("success");
                setToastShow(true);
                setConfirmDeleteModalOpen(false);
                close();
            } catch (error) {
                setDisableSubmit(false);
                if (error instanceof Error) Bugsnag.notify(error);
                setToastMessage("Ekki tókst að eyða einstaklingi");
                setToastType("error");
                setToastShow(true);
            }
        }

        return (
            <div className="KCL_edit-individual-modal">
                <Form>
                    {data.ssn
                        ? <div className="form__body">
                            <div className="form__row">
                                <div className="form__col">
                                    <Input
                                        label="Kennitala"
                                        value={data.ssn}
                                        disabled
                                    />
                                </div>
                                <div className="form__col">
                                    <Input
                                        label="Nafn"
                                        value={data.name}
                                        disabled
                                    />
                                </div>
                            </div>
                            <div className="form__row">
                                <div className="form__col">
                                    <FormikInput
                                        label="Netfang"
                                        name="email"
                                        id="email"
                                        value={values.email}
                                    />
                                </div>
                            </div>
                        </div>
                        : <div className="foreign-individual-form">

                            {/* Name */}
                            <Input label="Nafn" value={data.name} disabled />

                            {/* Date of Birth */}
                            <DatePickerField
                                label="Fæðingardagur"
                                name="dateOfBirth"
                                id="dateOfBirth"
                                size="lg"
                                maxDate={new Date()}
                                popperPlacement="top"
                                showYearDropdown
                                showMonthDropdown
                                max_width
                            />

                            <div className="address-area">
                                {/* Address */}
                                <FormikInput label="Heimilisfang" name="address" id="address" value={values.address} />

                                {/* Postal Code */}
                                <FormikInput label="Póstnúmer" name="areaCode" id="areaCode" value={values.areaCode} />
                            </div>

                            {/* Region */}
                            <FormikInput label="Sveitarfélag" name="region" id="region" value={values.region} />

                            {/* Country */}
                            <DropSelectSearch
                                usePortal={true}
                                error={
                                    countryCodeFieldMeta.touched && countryCodeFieldMeta.error ? countryCodeFieldMeta.error : undefined
                                }
                                selectSize="lg"
                                label="Búsetuland"
                                name="country"
                                id="country"
                                options={CountryList}
                                defaultValue={values.country}
                                onSelectChange={(cc: string) => setFieldValue("country", cc)}
                            />

                            {/* Nationality */}
                            <DropSelectSearch
                                usePortal={true}
                                error={
                                    countryCodeFieldMeta.touched && countryCodeFieldMeta.error ? countryCodeFieldMeta.error : undefined
                                }
                                selectSize="lg"
                                label="Ríkisfang"
                                name="nationality"
                                id="nationality"
                                options={CountryList}
                                defaultValue={values.nationality}
                                onSelectChange={(cc: string) => setFieldValue("nationality", cc)}
                            />

                            {/* Email */}
                            <FormikInput label="Netfang" name="email" id="email" value={values.email} />

                            {/* Supervisor */}
                            <DropSelect
                                clearInput={true}
                                usePortal={true}
                                label="Umsjónaraðili (valkvætt)"
                                options={employees.map(emp => ({
                                    id: String(emp.webUserId),
                                    label: emp.fullname,
                                    value: String(emp.webUserId)
                                }))}
                                defaultValue={values.supervisorId ? String(values.supervisorId) : null}
                                onChange={val => setFieldValue("supervisorId", val ? Number(val) : null)}
                                size="lg"
                            />
                        </div>
                    }
                    <div className="form__actions">
                        <Button
                            type="button"
                            buttonType="secondary"
                            size="lg"
                            onClick={() => close()}
                        >
                            Hætta við
                        </Button>
                        {!sendNotification &&
                            <Button
                                type="button"
                                buttonType="secondary"
                                size="lg"
                                onClick={() => setConfirmDeleteModalOpen(true)}
                                icon={faTrashCan}
                            >
                                Eyða
                            </Button>
                        }
                        <Button
                            type="submit"
                            buttonType="primary"
                            size="lg"
                            disabled={disableSubmit}
                        >
                            {!sendNotification ? 'Vista' : 'Staðfesta og senda áreiðanleikakönnun'}
                        </Button>
                    </div>
                </Form>
                <Modal
                    className="confirm-delete-modal"
                    show={confirmDeleteModalOpen}
                    onHide={() => setConfirmDeleteModalOpen(false)}
                    header="Eyða viðskiptavini"
                    body={
                        <div className="alertBody">
                            <Alert
                                type="alert"
                                headText={`Þú ert að fara eyða ${data.name}.`}
                            />
                            <div className="infoSection">
                                <p className="paragraph">
                                    Viltu halda áfram?
                                </p>
                            </div>
                            <div className="actions">
                                <Button onClick={() => setConfirmDeleteModalOpen(false)} buttonType="secondary" size="lg">Hætta við</Button>
                                <Button onClick={handleDelete} buttonType="primary" size="lg" disabled={disableSubmit}>Staðfesta</Button>
                            </div>
                        </div>
                    }
                />
            </div>
        );
    };

const EditIndividualModal: React.FC<IProps> = ({
    show,
    close,
    setToastMessage,
    setToastType,
    setToastShow,
    data,
    employees,
    individuals,
    setIndividuals,
    sendNotification,
    riskScore,
    setRiskScore
}) => {
    const [disableSubmit, setDisableSubmit] = React.useState(false);
    const subFormComponent = useMemo(() => {
        return (props: { values: IFormValues }) => {
            if (!data) return null;
            return <SubFormComponent
                values={props.values}
                data={data}
                employees={employees}
                close={close}
                onDelete={() => { }}
                setToastMessage={setToastMessage}
                setToastType={setToastType}
                setToastShow={setToastShow}
                updateIndividuals={updateIndividuals}
                disableSubmit={disableSubmit}
                setDisableSubmit={setDisableSubmit}
                sendNotification={sendNotification}
            />;
        }
    }, [close, data, disableSubmit]);

    const modalBody = () => {
        if (!data) return null;
        return (
            <Formik
                initialValues={{
                    id: data.individualCustomerId,
                    address: data.address ?? '',
                    areaCode: data.areaCode ?? '',
                    region: data.region ?? '',
                    country: data.country ?? '',
                    nationality: data.nationality ?? '',
                    email: data.email ?? '',
                    foreign: data.ssn ? false : true,
                    name: data.name ?? '',
                    ssn: data.ssn ?? '',
                    dateOfBirth: data.dateOfBirth ?? null,
                    supervisorId: data.supervisorId ?? null
                }}
                validate={validate}
                onSubmit={handleSubmit}
                component={subFormComponent}
            />
        );
    }

    const handleSubmit = async (values: IFormValues) => {
        try {
            setDisableSubmit(true);

            // Prepare payload - format dateOfBirth as ISO string for DateTime in .NET
            const payload = {
                ...values,
                dateOfBirth: values.dateOfBirth ? new Date(values.dateOfBirth).toISOString() : null
            };

            const url = `${GET_KELDAN_API_URL()}/DueDiligence/UpdateIndividual`;
            const response = await fetch(url, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json"
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorData = await response.text();
                setToastMessage(errorData || "Ekki tókst að breyta einstaklingi");
                setToastType("error");
                setToastShow(true);
                return;
            }

            if (sendNotification) {
                const notifyResponse = await fetch(`/DueDiligence/SendIndividualNotification?individualId=${data?.individualCustomerId}`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' }
                });

                if (!notifyResponse.ok) {
                    const errorData = await notifyResponse.text();
                    setToastMessage(errorData || "Ekki tókst að senda áreiðanleikakönnun");
                    setToastType("error");
                    setToastShow(true);
                    return;
                }

                setToastMessage("Áreiðanleikakönnun send");
            } else {
                setToastMessage("Einstaklingur uppfærður");
            }

            updateIndividuals(payload);
            if (riskScore && setRiskScore) {
                setRiskScore({ ...riskScore, dateSent: new Date().toISOString() });
            }
            setToastType("success");
            setToastShow(true);
            close();
        } catch (error) {
            if (error instanceof Error) Bugsnag.notify(error);
            setToastMessage("Ekki tókst að breyta einstaklingi");
            setToastType("error");
            setToastShow(true);
        } finally {
            setDisableSubmit(false);
        }
    }

    /**
     * Updates the local individuals state to reflect changes made in the form
     * This prevents needing to refetch all individual data after an update
     * Uses functional setState to avoid stale closure issues
     * 
     * @param values - The form values containing updated individual information
     * @param deleted - If true, removes the individual from the list instead of updating
     */
    const updateIndividuals = (values: IFormValues, deleted: boolean = false) => {
        // Early return if setIndividuals is not provided
        if (!setIndividuals) return;

        // Use functional update to get the latest state
        setIndividuals((prevIndividuals) => {
            // Check if individuals data is valid
            if (!prevIndividuals || prevIndividuals instanceof Error) return prevIndividuals;

            // Handle deletion case - filter out the individual
            if (deleted) {
                const filteredIndividuals = prevIndividuals.filter(
                    individual => individual.individualCustomerId !== values.id
                );
                return filteredIndividuals;
            }

            // Handle update case - map through and update the matching individual
            const updatedIndividuals = prevIndividuals.map(individual => {
                // Only update the individual that matches the id
                if (individual.individualCustomerId === values.id) {

                    // Create updated individual by preserving all existing fields
                    // and only updating the fields that are in the form
                    const updated = {
                        ...individual,
                        // Update only the fields from the form values
                        // Note: We do NOT update individualCustomerId - it stays the same
                        address: values.address,
                        areaCode: values.areaCode,
                        region: values.region,
                        country: values.country,
                        email: values.email,
                        name: values.name,
                        dateOfBirth: values.dateOfBirth,
                        supervisorId: values.supervisorId,
                        // Only update ssn if it exists in values (for foreign individuals it might be empty)
                        ...(values.ssn && { ssn: values.ssn })
                    };

                    return updated;
                }
                // Return unchanged individual if ID doesn't match
                return individual;
            });

            return updatedIndividuals;
        });
    }

    return <Modal
        className="edit-individual-modal"
        show={show}
        onHide={close}
        hasCloseButton
        header={<h3>{sendNotification ? "Senda áreiðanleikakönnun" : "Breyta einstaklingi"}</h3>}
        body={modalBody()}
    />
};

export const validate = (values: IFormValues) => {
    const errors: { [key: string]: string } = {};
    if (!values.foreign) {
        if (!values.email) errors.email = 'Netfang er nauðsynlegt';
    } else {
        if (!values.address) errors.address = 'Heimilisfang er nauðsynlegt';
        if (!values.areaCode) errors.areaCode = 'Póstnúmer er nauðsynlegt';
        if (!values.region) errors.region = 'Sveitarfélag er nauðsynlegt';
        if (!values.country) errors.country = 'Land er nauðsynlegt';
        if (!values.email) errors.email = 'Netfang er nauðsynlegt';
        if (!values.dateOfBirth) errors.dateOfBirth = 'Fæðingardagur er nauðsynlegur';
    }

    return errors;
};

export default EditIndividualModal;